'use client';

/**
 * Library catalog page.
 *
 * Module: app/library/page
 * Layer:  page
 *
 * Purpose:
 *     Composes the library experience: a search + filter toolbar, the
 *     authenticated "My checkouts" and "Reading assignments" panels, and the
 *     responsive catalog grid. All data + action state lives in the
 *     {@link useLibrary} controller; this file only wires the sections so it
 *     stays thin and within the CC<=5 / <=60-line gates.
 *
 * AI context:
 *     - Entry points called by: Next.js App Router for `/library`.
 *     - Depends on: components/Nav, app/library/use-library + book-grid,
 *       lib/i18n.
 *     - Side effects: none directly (the controller owns network I/O).
 *     - Invariants: renders a loading state until the first catalog load
 *       settles; an action error surfaces a dismissible banner.
 *     - Replacement cost: LOW.
 */

import Nav from '@/components/Nav';
import { useTranslation } from '@/lib/i18n';
import { AssignmentsSection, BookGrid, CheckoutsSection, Toolbar } from './book-grid';
import { type LibraryController, useLibrary } from './use-library';

/** Centered spinner shown until the first catalog load settles. */
function LoadingState(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className='py-12 text-center'>
      <div className='mx-auto mb-4 size-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent' />
      <p className='opacity-70'>{t('library.loading')}</p>
    </div>
  );
}

/** Inline banner for the most recent checkout / return failure. */
function ActionErrorBanner(props: {
  readonly error: LibraryController['actionError'];
}): React.JSX.Element | null {
  const { t } = useTranslation();
  if (props.error === null) {
    return null;
  }
  const messageKey =
    props.error === 'checkout' ? 'library.checkoutFailed' : 'library.returnFailed';
  return (
    <div className='mb-6 rounded-lg bg-red-100 px-4 py-3 text-sm text-red-700' role='alert'>
      {t(messageKey)}
    </div>
  );
}

/** Loaded sections: personal panels above the catalog grid. */
function LibrarySections(props: { readonly ctrl: LibraryController }): React.JSX.Element {
  const { ctrl } = props;
  return (
    <>
      <CheckoutsSection ctrl={ctrl} />
      <AssignmentsSection ctrl={ctrl} />
      <BookGrid ctrl={ctrl} />
    </>
  );
}

/** Page body: heading, toolbar, error banner, then loading or sections. */
function LibraryContent(): React.JSX.Element {
  const { t } = useTranslation();
  const ctrl = useLibrary();
  return (
    <main className='mx-auto max-w-7xl px-4 py-8'>
      <div className='mb-8'>
        <h1 className='mb-2 text-3xl font-bold'>{t('library.title')}</h1>
        <p className='opacity-70'>{t('library.subtitle')}</p>
      </div>
      <Toolbar ctrl={ctrl} />
      <ActionErrorBanner error={ctrl.actionError} />
      {ctrl.loading ? <LoadingState /> : <LibrarySections ctrl={ctrl} />}
    </main>
  );
}

/** Route entry: app shell nav plus the catalog content. */
export default function LibraryPage(): React.JSX.Element {
  return (
    <div>
      <Nav />
      <LibraryContent />
    </div>
  );
}
